import { Injectable } from '@angular/core';
import * as JSZip from 'jszip';
import { Workbook, Topic } from 'xmind';
import { Buffer } from 'buffer';
import { TodoItemNode } from 'src/app/model/notes.model';

interface ZipperOptions {
  path: string;
  workbook: Workbook;
  filename?: string;
}

class Base {}

class Zipper extends Base {
  zip: JSZip;
  manifest: any;
  filename: string;
  path: string;
  workbook: Workbook;

  constructor(options: ZipperOptions) {
    super();
    this.zip = new JSZip();
    this.manifest = {
      "file-entries": {
        "content.json": {
          "media-type": "application/json"
        },
        "metadata.json": {
          "media-type": "application/json"
        },
        "manifest.json": {
          "media-type": "application/json"
        },
        "META-INF/": {
          "media-type": "application/vnd.xmind.folder"
        },
        "META-INF/manifest.xml": {
          "media-type": "text/xml"
        }
      }
    };
    this.filename = options.filename || 'default.xmind';
    this.path = options.path;
    this.workbook = options.workbook;
  }

  target(): any {
    return this.zip;
  }

  async save(contentJson: any, metadataJson: any, manifestJson: any, metaInfManifestXml: string): Promise<any> {
    this.addJSONContent('content.json', contentJson);
    this.addJSONContent('metadata.json', metadataJson);
    this.addJSONContent('manifest.json', manifestJson);
    this.addXMLContent('META-INF/manifest.xml', metaInfManifestXml);

    return this.zip.generateAsync({ type: 'blob' });
  }

  updateManifestMetadata(key: string, content: Buffer): this {
    this.zip.file(key, content);
    this.manifest['file-entries'][key] = {
      "media-type": "image/png"
    };
    return this;
  }

  private addJSONContent(key: string, content: any): void {
    this.zip.file(key, JSON.stringify(content, null, 2));
  }

  private addXMLContent(key: string, content: string): void {
    this.zip.file(key, content);
  }

  addImage(fileName: string, fileType: string, content: Buffer): void {
    const filePath = `resources/${fileName}`;
    this.zip.file(filePath, content);
    this.manifest['file-entries'][filePath] = {
      "media-type": fileType
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class XMindService {
  constructor() {}

  async exportToXMind(rootNode: TodoItemNode): Promise<string> {
    try {
      const workbook = new Workbook();
      const sheet = workbook.createSheet('Sheet Title', rootNode.item);
      const rootTopic = new Topic({ sheet });

      const zip = new Zipper({ path: '/tmp', workbook, filename: 'MyFirstMap.xmind' });

      await this.addChildrenToTopic(rootTopic, rootNode.children, zip);

      const contentJson = this.generateContentJson(rootNode);
      const metadataJson = this.generateMetadataJson();
      const manifestJson = this.generateManifestJson(zip.manifest);
      const metaInfManifestXml = this.generateMetaInfManifestXml();
      const status = await zip.save(contentJson, metadataJson, manifestJson, metaInfManifestXml);
      if (status) {
        const url = window.URL.createObjectURL(status);
        return url;
      } else {
        throw new Error('Error saving XMind file.');
      }
    } catch (error) {
      throw error;
    }
  }

  private async addChildrenToTopic(parentTopic: Topic, children: TodoItemNode[], zip: Zipper): Promise<void> {
    for (const child of children) {
      const childTopic = parentTopic.add({ title: child.item });
      if (child.file) {
        const buffer = await this.readFileAsBuffer(child.file);
        zip.addImage(child.fileName, child.file.type , buffer);
      }
      if (child.has_child) {
        await this.addChildrenToTopic(childTopic, child.children, zip);
      }
    }
  }

  private readFileAsBuffer(file: File): Promise<Buffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(Buffer.from(reader.result as ArrayBuffer));
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  private generateContentJson(rootNode: TodoItemNode): any {
    const content = [
      {
        "id": "sheet-id",
        "class": "sheet",
        "title": "Map 1",
        "rootTopic": {
          "id": rootNode.id,
          "class": "topic",
          "title": rootNode.item,
          "structureClass": "org.xmind.ui.map.clockwise",
          "children": {
            "attached": rootNode.children.map(child => this.generateTopicJson(child))
          }
        }
      }
    ];
    return content;
  }

  private generateTopicJson(node: TodoItemNode): any {
    const topic: any = {
      "id": node.id,
      "title": node.item,
    };

    if (node.file) {
      topic.image = {
        "src": `xap:resources/${node.fileName}`
      };
    }

    if (node.has_child) {
      topic.children = {
        "attached": node.children.map(child => this.generateTopicJson(child))
      };
    }

    return topic;
  }

  private generateMetadataJson(): any {
    const metadata = {
      "creator": {
        "name": "Exporting tool",
        "version": "1.0.0"
      },
      "activeSheetId": "sheet-id"
    };
    return metadata;
  }

  private generateManifestJson(manifest: any): any {
    return manifest;
  }

  private generateMetaInfManifestXml(): string {
    const metaInfManifest = `
      <manifest xmlns="urn:xmind:xmap:xmlns:manifest:1.0">
        <file-entry full-path="content.json" media-type="application/json"/>
        <file-entry full-path="metadata.json" media-type="application/json"/>
        <file-entry full-path="manifest.json" media-type="application/json"/>
        <file-entry full-path="META-INF/" media-type=""/>
        <file-entry full-path="META-INF/manifest.xml" media-type="text/xml"/>
      </manifest>
    `;
    return metaInfManifest.trim();
  }
}
